import { useEffect, useState } from 'react';

import debounce from '../utils/debounce';

/**
 * This hook is intended to be used to force a relayout when the window is resized.
 *
 * This is needed on iOS due to a bug generated by Safari caused, in turn, by the
 * address bar animation that can be found in this browser. The animation itself
 * does not trigger a relayout, so elements using 100vh will overflow when the address
 * bar size is changed.
 *
 * To trigger a relayout, add the following as a child of the component using 100vh:
 * `{resizeCounter % 2 === 0 ? <div /> : null}`
 *
 * @returns {number} Current count of window resizes modulo Number.MAX_SAFE_INTEGER
 */
function useResizeCounter(debounceMs = 100): number {
  const [resizeCount, setResizeCount] = useState<number>(0);

  useEffect(() => {
    const resizeListener = debounce(() => {
      setResizeCount((resizeCount + 1) % Number.MAX_SAFE_INTEGER);
    }, debounceMs);

    window.addEventListener('resize', resizeListener);

    return () => {
      window.removeEventListener('resize', resizeListener);
    };
  }, [debounceMs, resizeCount]);

  return resizeCount;
}

export default useResizeCounter;
